import React, { useEffect, useRef } from 'react';
import { bool, func, node, number, string } from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import { PrimaryButton, SecondaryButton, IconSpinner } from '../../components';

import css from './TransactionPanel.css';

// The invoice path's last step before the accept (offer accept confirm step
// spec §3): the VAT is settled here, once the payment method is fixed. The
// modal owns the state, the preview effects and the handlers; this only lays
// out what it is given.
const CustomOfferConfirmStep = props => {
  const {
    offerTitle,
    gateNotice,
    checking,
    vatExplanation,
    pricingBox,
    paymentTermDays,
    acceptError,
    onBack,
    onAccept,
    acceptInProgress,
    acceptDisabled,
    showHoldHint,
  } = props;

  // The modal's scroll layer keeps its offset from the long offer step, so
  // bring the title (and what follows it) back into view when the step opens.
  const titleRef = useRef(null);
  useEffect(() => {
    const el = titleRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
  }, []);

  return (
    <>
      <h2 ref={titleRef} className={css.customOfferModalTitle}>
        <FormattedMessage id="CustomOfferModal.confirmTitle" />
      </h2>
      {offerTitle ? (
        <div className={css.modalSection}>
          <h3 className={css.modalOfferTitle}>{offerTitle}</h3>
        </div>
      ) : null}
      {gateNotice}
      {checking ? (
        <div className={css.modalSection}>
          <div className={css.vatNoticeRow} role="status" aria-live="polite">
            <IconSpinner rootClassName={css.vatNoticeSpinner} />
            <p className={css.vatNoticeText}>
              <FormattedMessage id="VatPreview.checking" />
            </p>
          </div>
        </div>
      ) : null}
      {vatExplanation ? <div className={css.modalSection}>{vatExplanation}</div> : null}
      {pricingBox ? <div className={css.modalSection}>{pricingBox}</div> : null}
      <div className={css.modalSection}>
        <p className={css.modalHelpNote}>
          <FormattedMessage
            id="TransactionPanel.acceptProModalMessage"
            values={{ days: paymentTermDays }}
          />
        </p>
      </div>
      <div className={css.modalActionButtons}>
        {acceptError}
        <div className={css.confirmStepButtons}>
          <SecondaryButton type="button" onClick={onBack}>
            <FormattedMessage id="CustomOfferModal.back" />
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={onAccept}
            inProgress={acceptInProgress}
            disabled={acceptDisabled}
          >
            <FormattedMessage id="CustomOfferModal.acceptWithInvoice" />
          </PrimaryButton>
        </div>
        {showHoldHint ? (
          <p className={css.vatNoticeHint}>
            <FormattedMessage id="VatPreview.holdAcceptHint" />
          </p>
        ) : null}
      </div>
    </>
  );
};

CustomOfferConfirmStep.defaultProps = {
  offerTitle: null,
  gateNotice: null,
  checking: false,
  vatExplanation: null,
  pricingBox: null,
  acceptError: null,
  acceptInProgress: false,
  acceptDisabled: false,
  showHoldHint: false,
};

CustomOfferConfirmStep.propTypes = {
  offerTitle: string,
  gateNotice: node,
  checking: bool,
  vatExplanation: node,
  pricingBox: node,
  paymentTermDays: number.isRequired,
  acceptError: node,
  onBack: func.isRequired,
  onAccept: func.isRequired,
  acceptInProgress: bool,
  acceptDisabled: bool,
  showHoldHint: bool,
};

export default CustomOfferConfirmStep;
